import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { getMarkRange } from '@tiptap/core';
import { ActionIcon, Group, Tooltip, Text, Box } from '@mantine/core';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Minus,
  TextQuote,
  Link as LinkIcon,
  Unlink,
} from 'lucide-react';
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import classes from './RichTextEditor.module.css';
import { sanitizeHtml } from './sanitize';
import { LinkDialog } from './LinkDialog';

/**
 * MED-1131 AC9 — helper note clarifying when hyperlinks become clickable.
 * Shown below Session Notes editors in edit mode (passed via `helperText`).
 */
export const LINK_CLICKABILITY_HELPER_TEXT =
  'Hyperlinks become clickable when viewing saved notes on the post-session page.';

/**
 * MED-1131 AC6 — normalize a user-entered URL so the resulting link is valid.
 * A value without a scheme (e.g. `example.com`) is prefixed with `https://`;
 * anything already carrying a scheme (`https:`, `mailto:`, `tel:` …) is left
 * as-is. Returns '' for blank input (the dialog blocks submit in that case).
 */
function normalizeUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (trimmed === '') return '';
  // Already has a scheme like `https:`, `mailto:`, `tel:`.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * MED-1131 (QA round 2) — non-inclusive link mark.
 *
 * TipTap's Link mark ties `inclusive` to its `autolink` option
 * (`inclusive() { return this.options.autolink }`), and `autolink` defaults to
 * `true`. An *inclusive* mark is extended to cover text typed at its right
 * boundary, so typing immediately after an inserted link folded the new text
 * into the link — the bleed QA reported (only a new block escaped it).
 *
 * We force `inclusive: false` while leaving `autolink` on (paste/type URL
 * detection still works — autolink applies the mark over matched ranges
 * directly and does not rely on the inclusive flag). StarterKit's bundled link
 * is disabled below and replaced with this one.
 */
const NonInclusiveLink = Link.extend({ inclusive: () => false });

export interface RichTextEditorProps {
  /** Current HTML content */
  value: string;
  /** Called when content changes with sanitized HTML */
  onChange: (html: string) => void;
  /** Optional label above the editor */
  label?: string;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Minimum height of the editor in pixels */
  minHeight?: number;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Track focus state — useful for "insert merge tag into last-focused field" patterns (MED-901). */
  onFocus?: () => void;
  /**
   * MED-1014 — which formatting controls to show, as ordered groups (dividers
   * render between groups). Omit for the full default toolbar — every existing
   * consumer that doesn't pass this is unchanged. When provided, the toolbar is
   * kept to a single non-wrapping row.
   */
  toolbar?: RichTextControl[][];
  /** MED-1014 — let the editor grow to fill its container's height. */
  fillHeight?: boolean;
  /**
   * MED-1014 — drop the editor's own border/radius/focus ring so it sits flush
   * inside a host card that provides the frame (e.g. the Session Notes card).
   */
  seamless?: boolean;
  /**
   * MED-1030 — optional node rendered right-aligned in the toolbar row (e.g. a
   * "Saved" status indicator). Backward-compatible: omit and the toolbar is
   * unchanged.
   */
  toolbarRight?: ReactNode;
  /**
   * MED-1131 AC9 — optional helper note rendered below the editor content. The
   * editor only ever renders in edit mode (display mode uses dangerouslySet-
   * InnerHTML elsewhere), so passing this shows the note in-session and in
   * post-session edit but never in display mode. Hidden while `disabled` (the
   * read-only preview is not an edit affordance). Session Notes editors pass
   * LINK_CLICKABILITY_HELPER_TEXT.
   */
  helperText?: ReactNode;
}

/** Formatting controls the toolbar can render (MED-1014). */
export type RichTextControl =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'bulletList'
  | 'orderedList'
  | 'horizontalRule'
  | 'blockquote'
  | 'link';

/** The full toolbar, grouped — the default when `toolbar` is omitted. */
const DEFAULT_TOOLBAR: RichTextControl[][] = [
  ['bold', 'italic', 'underline', 'strike'],
  ['h1', 'h2', 'h3'],
  ['bulletList', 'orderedList'],
  ['horizontalRule', 'blockquote'],
  ['link'],
];

/**
 * Imperative handle exposed when consumers pass a `ref` (MED-901).
 *
 * Existing callers that don't pass a ref are unaffected — the handle is opt-in.
 * Use `insertText` to insert plain text at the current cursor position (e.g.,
 * merge tag chips in the Send Recap Email modal).
 */
export interface RichTextEditorHandle {
  /** Insert text at the current cursor position, then refocus the editor. */
  insertText: (text: string) => void;
  /** Focus the editor (places cursor at the end if no prior selection). */
  focus: () => void;
}

interface MenuBarProps {
  editor: Editor | null;
  /** Resolved set of control groups to render. */
  toolbar: RichTextControl[][];
  /** Keep the toolbar on a single row (no wrap). */
  nowrap: boolean;
  /** MED-1030 — right-aligned slot in the toolbar row (e.g. a "Saved" status). */
  rightSlot?: ReactNode;
}

function MenuBar({ editor, toolbar, nowrap, rightSlot }: MenuBarProps) {
  // MED-1131 — Text + URL dialog state, replacing the old window.prompt.
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkInitialText, setLinkInitialText] = useState('');
  const [linkInitialUrl, setLinkInitialUrl] = useState('');

  // Open the dialog, pre-filled from the current selection (AC3) or, when the
  // cursor sits inside an existing link, that link's text + href (AC7).
  const openLinkDialog = useCallback(() => {
    if (!editor) return;

    const { state } = editor;
    const { from, to, empty, $from } = state.selection;
    const previousUrl: string = editor.getAttributes('link').href ?? '';

    let initialText = '';
    if (!empty) {
      // AC3 — pre-fill with the highlighted text.
      initialText = state.doc.textBetween(from, to, ' ');
    } else if (previousUrl) {
      // AC7 — no selection but inside a link: read the link's full text by
      // resolving its mark range.
      const linkMark = state.schema.marks.link;
      const range = linkMark ? getMarkRange($from, linkMark) : undefined;
      if (range) initialText = state.doc.textBetween(range.from, range.to, ' ');
    }

    setLinkInitialText(initialText);
    setLinkInitialUrl(previousUrl);
    setLinkDialogOpen(true);
  }, [editor]);

  // Insert (or replace) a single text node carrying the link mark. Works
  // whether or not text was selected — fixing the empty-selection insert the
  // old prompt broke (AC4) and replacing the prior selection / existing link
  // with the edited text (AC5/AC7).
  const handleLinkSubmit = useCallback(
    (text: string, rawUrl: string) => {
      if (!editor) return;

      const href = normalizeUrl(rawUrl); // AC6 — add https:// when no scheme.
      if (href === '') return;
      // AC4 — blank text falls back to the URL so a usable link always appears.
      const displayText = text.trim() === '' ? href : text;

      let chain = editor.chain().focus();
      // AC7 — editing an existing link: expand the selection to the whole link
      // so insertContent replaces it wholesale (text + href).
      if (editor.getAttributes('link').href) {
        chain = chain.extendMarkRange('link');
      }

      chain
        .insertContent({
          type: 'text',
          text: displayText,
          marks: [
            {
              type: 'link',
              attrs: {
                href,
                target: '_blank',
                rel: 'noopener noreferrer',
              },
            },
          ],
        })
        .run();

      setLinkDialogOpen(false);
    },
    [editor]
  );

  if (!editor) return null;

  const iconButton = (
    label: string,
    active: boolean,
    onClick: () => void,
    icon: ReactNode
  ) => (
    <Tooltip label={label} position="top" withArrow>
      <ActionIcon
        variant={active ? 'filled' : 'subtle'}
        color={active ? 'medleyGreen' : 'gray'}
        onClick={onClick}
        size="sm"
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );

  const renderControl = (control: RichTextControl): ReactNode => {
    switch (control) {
      case 'bold':
        return iconButton(
          'Bold (Ctrl+B)',
          editor.isActive('bold'),
          () => editor.chain().focus().toggleBold().run(),
          <Bold size={16} />
        );
      case 'italic':
        return iconButton(
          'Italic (Ctrl+I)',
          editor.isActive('italic'),
          () => editor.chain().focus().toggleItalic().run(),
          <Italic size={16} />
        );
      case 'underline':
        return iconButton(
          'Underline (Ctrl+U)',
          editor.isActive('underline'),
          () => editor.chain().focus().toggleUnderline().run(),
          <UnderlineIcon size={16} />
        );
      case 'strike':
        return iconButton(
          'Strikethrough',
          editor.isActive('strike'),
          () => editor.chain().focus().toggleStrike().run(),
          <Strikethrough size={16} />
        );
      case 'h1':
        return iconButton(
          'Heading 1',
          editor.isActive('heading', { level: 1 }),
          () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          <Heading1 size={16} />
        );
      case 'h2':
        return iconButton(
          'Heading 2',
          editor.isActive('heading', { level: 2 }),
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          <Heading2 size={16} />
        );
      case 'h3':
        return iconButton(
          'Heading 3',
          editor.isActive('heading', { level: 3 }),
          () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          <Heading3 size={16} />
        );
      case 'bulletList':
        return iconButton(
          'Bullet List',
          editor.isActive('bulletList'),
          () => editor.chain().focus().toggleBulletList().run(),
          <List size={16} />
        );
      case 'orderedList':
        return iconButton(
          'Numbered List',
          editor.isActive('orderedList'),
          () => editor.chain().focus().toggleOrderedList().run(),
          <ListOrdered size={16} />
        );
      case 'horizontalRule':
        return iconButton(
          'Horizontal Rule',
          false,
          () => editor.chain().focus().setHorizontalRule().run(),
          <Minus size={16} />
        );
      case 'blockquote':
        return iconButton(
          'Block Quote',
          editor.isActive('blockquote'),
          () => editor.chain().focus().toggleBlockquote().run(),
          <TextQuote size={16} />
        );
      case 'link':
        return (
          <>
            {iconButton(
              'Add Link',
              editor.isActive('link'),
              openLinkDialog,
              <LinkIcon size={16} />
            )}
            {editor.isActive('link') && (
              <Tooltip label="Remove Link" position="top" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => editor.chain().focus().unsetLink().run()}
                  size="sm"
                >
                  <Unlink size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </>
        );
    }
  };

  const groups = toolbar.filter(group => group.length > 0);

  return (
    <>
      <Group
        gap={4}
        wrap={nowrap ? 'nowrap' : undefined}
        className={classes.toolbar}
      >
        {groups.map((group, groupIdx) => (
          <Fragment key={groupIdx}>
            {groupIdx > 0 && <Box className={classes.divider} />}
            {group.map(control => (
              <Fragment key={control}>{renderControl(control)}</Fragment>
            ))}
          </Fragment>
        ))}
        {rightSlot && <Box style={{ marginLeft: 'auto' }}>{rightSlot}</Box>}
      </Group>
      <LinkDialog
        opened={linkDialogOpen}
        initialText={linkInitialText}
        initialUrl={linkInitialUrl}
        onSubmit={handleLinkSubmit}
        onClose={() => setLinkDialogOpen(false)}
      />
    </>
  );
}

/**
 * RichTextEditor - WYSIWYG editor component for formatted text
 *
 * Built on Tiptap with a minimal, clean toolbar. Supports:
 * - Bold, italic, underline
 * - Headings (H1, H2, H3)
 * - Bullet and numbered lists
 * - Links
 *
 * Content is stored as sanitized HTML.
 *
 * @example
 * <RichTextEditor
 *   label="Internal Notes"
 *   value={notes}
 *   onChange={setNotes}
 *   placeholder="Add notes..."
 *   minHeight={200}
 * />
 */
export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(function RichTextEditor(
  {
    value,
    onChange,
    label,
    placeholder = 'Start typing...',
    minHeight = 150,
    disabled = false,
    error,
    onFocus,
    toolbar,
    fillHeight = false,
    seamless = false,
    toolbarRight,
    helperText,
  },
  ref
) {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        // Disable the bundled link so we can register a non-inclusive one
        // (MED-1131 QA round 2 — see NonInclusiveLink above).
        link: false,
      }),
      NonInclusiveLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const isEmpty = html === '<p></p>' || html === '';
      onChange(isEmpty ? '' : sanitizeHtml(html));
    },
    onFocus: () => {
      onFocus?.();
    },
  });

  // Update editor content when value prop changes externally.
  //
  // MED-1229 — this effect must fire *only* on genuinely external `value`
  // changes, never as an echo of the editor's own keystrokes. `onUpdate` stores
  // `sanitizeHtml(editor.getHTML())` back into `value`, so after a keystroke the
  // controlled `value` equals the *sanitized* serialization of the editor's
  // current document — not the raw `editor.getHTML()`. Comparing against the raw
  // output (`value !== editor.getHTML()`) treated that sanitizer round-trip as a
  // change and called `setContent()` on every keystroke, rebuilding the doc and
  // resetting the caret. That was invisible for plain text (sanitize is a no-op)
  // but showed up in Safari for block-level nodes (lists, block quotes): Safari's
  // DOM serializer, which DOMPurify runs through, emits those slightly
  // differently than ProseMirror's serializer, so the round-trip never matched
  // and the caret jumped mid-word. Fix: compare `value` against a *sanitized*
  // version of the editor's own output, so a serializer-only difference is not
  // mistaken for an external change.
  useEffect(() => {
    if (!editor) return;

    const currentContent = editor.getHTML();
    // Fast path: value already matches the editor's raw serialization
    // (the common case in Chrome/Firefox and for plain text).
    if (value === currentContent) return;

    const isEmpty = currentContent === '<p></p>' || currentContent === '';
    const newIsEmpty = value === '' || value === '<p></p>';

    // Both sides empty — nothing meaningful to sync.
    if (isEmpty && newIsEmpty) return;

    // The incoming `value` is the sanitized echo of what the editor already
    // holds (Safari lists/block quotes round-trip). Not an external change —
    // leave the document and caret untouched.
    if (!newIsEmpty && value === sanitizeHtml(currentContent)) return;

    editor.commands.setContent(value || '');
  }, [editor, value]);

  useImperativeHandle(
    ref,
    () => ({
      insertText: (text: string) => {
        if (!editor) return;
        // `focus()` first so the inserted text lands at the live selection
        // (Tiptap stores last selection but won't insert reliably if the
        // editor isn't focused — this matches the textarea cursor pattern).
        editor.chain().focus().insertContent(text).run();
      },
      focus: () => {
        editor?.chain().focus().run();
      },
    }),
    [editor]
  );

  return (
    <Box
      className={classes.wrapper}
      style={fillHeight ? { flex: 1, minHeight: 0 } : undefined}
    >
      {label && (
        <Text component="label" size="sm" fw={500} className={classes.label}>
          {label}
        </Text>
      )}
      <Box
        className={`${classes.editorContainer} ${error ? classes.error : ''} ${disabled ? classes.disabled : ''} ${seamless ? classes.seamless : ''} ${fillHeight ? classes.fill : ''}`}
        style={fillHeight ? { flex: 1, minHeight: 0 } : { minHeight }}
      >
        <MenuBar
          editor={editor}
          toolbar={toolbar ?? DEFAULT_TOOLBAR}
          nowrap={toolbar !== undefined}
          rightSlot={toolbarRight}
        />
        <EditorContent
          editor={editor}
          className={classes.content}
          placeholder={placeholder}
        />
      </Box>
      {helperText && !disabled && (
        <Text size="xs" c="dimmed" mt={6} px={16} data-testid="rte-helper-text">
          {helperText}
        </Text>
      )}
      {error && (
        <Text size="xs" c="red" mt={4} px={16}>
          {error}
        </Text>
      )}
    </Box>
  );
});

export default RichTextEditor;
